import React, { useEffect, useState } from 'react';
import { SubjectForm } from './SubjectForm';
import { SubjectDTO, SubjectCategory } from '../../../interfaces/curriculum';
import { useNavigate, useParams } from 'react-router-dom';
import categoryApi from '../../../api/categories';
import { SelectDropDown, Button, TextInput } from '@wot/components';
import { toast } from 'react-toastify';

const AddSubjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [formKey, setFormKey] = useState(0);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subjectData, setSubjectData] = useState<SubjectDTO | null>(null);
  const isEditMode = !!slug;

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      categoryApi
        .getSubjectById(slug)
        .then((res) => {
            setSubjectData(res);
          setSelectedCategoryName(res.category.name);
        })
        .catch((err) => {
          console.error('Failed to fetch subject details:', err);
          toast.error('Failed to fetch subject details.');
        });
    }
  }, [slug, isEditMode]);

  const fetchCategories = () => {
    setIsLoading(true);
    categoryApi
      .getSubjectCategory()
      .then((data) => {
        if (data) {
          setCategories(data);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch categories:', err);
        toast.error('Failed to fetch categories.');
      })
      .finally(() => setIsLoading(false));
  };

  const handleAddNewCategory = () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name.');
      return;
    }
    setIsLoading(true);
    const newCategory: SubjectCategory = {
      // id: newCategoryName.trim().toLowerCase().replace(/\s+/g, '-'), // Generate an ID from the name
      name: newCategoryName,
      description: newCategoryDescription
    };
    categoryApi
      .addSubjectCategory(newCategory)
      .then(() => {
        toast.success('New category added!');
        setNewCategoryName('');
        setNewCategoryDescription('');
        setShowAddCategory(false);
        fetchCategories(); // Refresh the list
      })
      .catch((err) => {
        toast.error('Failed to add new category.');
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  };

  const handleSaveSubject = async (subject: SubjectDTO) => {
    const selectedCategory = categories.find((c) => (c as SubjectCategory).name === selectedCategoryName) as
      | SubjectCategory
      | undefined;
    const selectedCategoryId = selectedCategory && (selectedCategory as any)._id ? (selectedCategory as any)._id : null;

    if (!selectedCategoryId) {
      toast.error('Please select a category before saving.');
      return;
    }

    const dataToSend = {
      categoryId: selectedCategoryId,
      subject: subject
    };
    setIsLoading(true);
    try {
      if (isEditMode) {
        if (!subjectData || !subjectData._id) {
          toast.error('Subject ID is missing. Cannot update subject.');
          setIsLoading(false);
          return;
        }
        await categoryApi.updateSubject(subjectData._id, dataToSend);
        toast.success('Subject updated successfully!');
      } else {
        await categoryApi.addNewSubject(dataToSend);
        toast.success('Subject added successfully!');
        setFormKey((prevKey) => prevKey + 1); // Reset form
        setSelectedCategoryName(''); // Reset category selection
      }
      navigate('/curriculum');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update subject.' : 'Failed to add subject.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const categoryNames = categories.map((category: SubjectCategory) => category.name);

  return (
    <div>
      <div className="p-4 max-w-sm mx-auto">
        {!showAddCategory ? (
          <div className="flex items-end gap-2">
            <div className="flex-grow">
              <SelectDropDown
                placeholder="Select subject Category"
                onChange={(value: string) => setSelectedCategoryName(value)}
                value={selectedCategoryName}
                options={categoryNames}
                // disabled={isEditMode}
              />
            </div>
            {!isEditMode && <Button text="Add New" onClick={() => setShowAddCategory(true)} />}
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-bold mb-2">Add New Category</h3>
            <TextInput placeholder="New category name" value={newCategoryName} onChange={setNewCategoryName} />
            <div className="mt-2">
              <TextInput
                placeholder="Description (optional)"
                value={newCategoryDescription}
                onChange={setNewCategoryDescription}
              />
            </div>
            <div className="flex gap-2 mt-2">
              <Button text="Save" onClick={handleAddNewCategory} disabled={isLoading} />
              <Button text="Cancel" onClick={() => setShowAddCategory(false)} />
            </div>
          </div>
        )}
      </div>
      {(selectedCategoryName || isEditMode) && (
        <SubjectForm key={formKey} onSave={handleSaveSubject} initialData={subjectData} isEditMode={isEditMode} />
      )}
    </div>
  );
};

export default AddSubjectDetail;
